import { forwardRef, ReactNode, Ref } from 'react';
import {
    ColorPicker as PrimeColorPicker,
    type ColorPickerProps as PrimeColorPickerProps,
} from 'primereact/colorpicker';
import { Controller, useFormContext } from 'react-hook-form';
import {
    buildRhfRules,
    type FieldValidate,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';

export interface ColorPickerProps extends PrimeColorPickerProps {
    name: string;
    /**
     * Custom react-hook-form validation function
     */
    validate?: FieldValidate;
    required?: boolean;
    'data-testid'?: string;
}

function ColorPickerComponent(
    props: ColorPickerProps,
    ref: Ref<PrimeColorPicker>,
): ReactNode {
    const { validate, required, ...restProps } = props;
    const formContext = useFormContext();
    const isControlled = typeof props.value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && props.name);
    warnAboutRegisterSpread({
        componentName: 'ColorPicker',
        isRHFControlled,
        onChange: props.onChange,
        ref,
    });

    const testId = props['data-testid'] || props.name;

    if (!isRHFControlled) {
        return (
            <PrimeColorPicker
                ref={ref}
                defaultColor="ffffff"
                {...restProps}
                data-testid={testId}
            />
        );
    }

    return (
        <Controller
            name={props.name}
            control={formContext!.control}
            rules={buildRhfRules({ required, validate })}
            render={({ field }) => (
                <PrimeColorPicker
                    defaultColor="ffffff"
                    {...restProps}
                    {...field}
                    data-testid={testId}
                    ref={mergeRefs(ref, field.ref)}
                    value={field?.value || props.value || ''}
                    onChange={(e) => {
                        if (props.onChange) {
                            props.onChange({
                                ...e,
                                value: `#${e.value}`,
                            });
                        }

                        field.onChange(`#${e.value}`);
                    }}
                    onBlur={(e) => {
                        field.onBlur();

                        if (props.onBlur) {
                            props.onBlur(e);
                        }
                    }}
                />
            )}
        />
    );
}

export const ColorPicker = forwardRef(ColorPickerComponent);
